import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import MealPrepLoader from '../MealPrepLoader';
import NotificationLiveToasts from './NotificationLiveToasts';
import { useAuth } from '../../context/AuthContext';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  parseNotificationMeta,
  subscribeToNotifications,
} from '../../services/notificationService';
import { useSupportTicketResolved } from '../../context/SupportTicketResolvedContext';
import {
  applyNotificationClick,
  resolveNotificationClick,
} from '../../utils/notificationTarget';

const timeAgo = (dateStr) => {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const TYPE_META = {
  like: { icon: 'ThumbsUp', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-950/30' },
  dislike: { icon: 'ThumbsDown', color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-800' },
  upvote: { icon: 'ThumbsUp', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-950/30' },
  downvote: { icon: 'ThumbsDown', color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-800' },
  comment: { icon: 'MessageCircle', color: 'text-indigo-500', bg: 'bg-indigo-50 dark:bg-indigo-950/30' },
  reply: { icon: 'Reply', color: 'text-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  broadcast_post: { icon: 'Megaphone', color: 'text-amber-500', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  resource_pending: { icon: 'Clock', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  resource_approved: { icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  resource_rejected: { icon: 'XCircle', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/30' },
  activity_share_pending: { icon: 'Globe', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-950/30' },
  activity_share_approved: { icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  activity_share_rejected: { icon: 'XCircle', color: 'text-red-500', bg: 'bg-red-50 dark:bg-red-950/30' },
  activity_catalog_flagged: { icon: 'Flag', color: 'text-rose-600', bg: 'bg-rose-50 dark:bg-rose-950/30' },
  activity_catalog_flag_approved: { icon: 'CheckCircle', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  activity_catalog_reviewed: { icon: 'ClipboardCheck', color: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-950/30' },
  support_ticket_new: { icon: 'Bug', color: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-950/30' },
  support_ticket_resolved: { icon: 'CheckCircle2', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  support_ticket_reply: { icon: 'MessageSquare', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-950/30' },
  support_ticket_followup: { icon: 'RefreshCw', color: 'text-rose-500', bg: 'bg-rose-50 dark:bg-rose-950/30' },
  onboarding_completed: { icon: 'UserRoundCog', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  signup_lead: { icon: 'Inbox', color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-950/30' },
  community_group_message: { icon: 'MessageCircle', color: 'text-rose-500', bg: 'bg-rose-50 dark:bg-rose-950/30' },
  community_idea_status: { icon: 'Lightbulb', color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-950/30' },
};

const TOAST_TYPES = new Set(['signup_lead', 'support_ticket_new', 'onboarding_completed']);

const formatNotificationMessage = (notif) => {
  const message = parseNotificationMeta(notif?.message).cleanMessage || '';

  if (notif?.type === 'signup_lead') {
    return message
      .replace(/\s*\((activity-planner|meal-manager|both)\)\s*\.?$/i, '')
      .replace(/\s+for apps\.?$/i, '.')
      .trim() || 'New signup received.';
  }

  if (/liked your post/i.test(message)) {
    return message.replace(/^.*? liked your post/i, 'Someone liked your post');
  }
  if (/disliked your post/i.test(message)) {
    return message.replace(/^.*? disliked your post/i, 'Someone disliked your post');
  }
  if (/liked your comment/i.test(message)) {
    return message.replace(/^.*? liked your comment/i, 'Someone liked your comment');
  }
  if (/disliked your comment/i.test(message)) {
    return message.replace(/^.*? disliked your comment/i, 'Someone disliked your comment');
  }
  if (/commented on your post/i.test(message)) {
    return message.replace(/^.*? commented on your post/i, 'Someone commented on your post');
  }
  if (/replied to your comment/i.test(message)) {
    return message.replace(/^.*? replied to your comment/i, 'Someone replied to your comment');
  }

  if (/support ticket has been resolved/i.test(message)) {
    const short = message.replace(/\.\s*Please refresh.*$/i, '').trim();
    return `${short}. Click for more details`;
  }
  if (/new support ticket/i.test(message) || /follow-up on ticket/i.test(message)) {
    return message;
  }

  return message;
};

const buildLiveToast = (notif) => {
  const parsed = parseNotificationMeta(notif?.message);
  const body = formatNotificationMessage(notif);
  if (notif.type === 'signup_lead') {
    return {
      id: `toast-${notif.id}`,
      notificationId: notif.id,
      type: notif.type,
      icon: 'Inbox',
      eyebrow: 'New signup',
      title: 'Someone just registered on maap.care',
      body: body || 'Open signup leads to review their details.',
      cta: 'Review signup',
    };
  }
  if (notif.type === 'support_ticket_new') {
    return {
      id: `toast-${notif.id}`,
      notificationId: notif.id,
      type: notif.type,
      icon: 'Bug',
      eyebrow: 'Support',
      title: 'New support ticket',
      body: body || 'A new ticket needs attention.',
      cta: 'Open ticket',
    };
  }
  if (notif.type === 'onboarding_completed') {
    return {
      id: `toast-${notif.id}`,
      notificationId: notif.id,
      type: notif.type,
      icon: 'UserCheck',
      eyebrow: 'Onboarding',
      title: 'Customer finished setup',
      body: body || 'Review their completed onboarding.',
      cta: 'Open onboarding',
    };
  }
  return {
    id: `toast-${notif.id}`,
    notificationId: notif.id,
    type: notif.type,
    icon: 'Bell',
    eyebrow: parsed.sourceLabel || 'Notification',
    title: 'New notification',
    body,
    cta: 'Open',
  };
};

const NotificationBell = () => {
  const { user, isFreePlan, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const { openResolvedTicket } = useSupportTicketResolved();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [liveToasts, setLiveToasts] = useState([]);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  const dismissToast = useCallback((id) => {
    setLiveToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pushLiveToast = useCallback((notif) => {
    if (!TOAST_TYPES.has(notif?.type)) return;
    if (notif.type === 'signup_lead' && isSuperAdmin === false) return;

    const toast = buildLiveToast(notif);
    setLiveToasts((prev) => [toast, ...prev.filter((item) => item.id !== toast.id)].slice(0, 3));
  }, [isSuperAdmin]);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      const [notifs, count] = await Promise.all([
        getNotifications(user.id, { limit: 30 }),
        getUnreadCount(user.id),
      ]);
      setNotifications(notifs);
      setUnreadCount(count);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    loadNotifications();

    const channel = subscribeToNotifications(user.id, (newNotif) => {
      setNotifications((prev) => [newNotif, ...prev].slice(0, 30));
      setUnreadCount((prev) => prev + 1);
      pushLiveToast(newNotif);
    });

    const interval = setInterval(loadNotifications, 60000);

    return () => {
      channel?.unsubscribe();
      clearInterval(interval);
    };
  }, [user?.id, loadNotifications, pushLiveToast]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        bellRef.current && !bellRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openNotification = async (notif, { closeDropdown = true } = {}) => {
    if (!notif) return;

    if (!notif.is_read) {
      await markAsRead(notif.id);
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, is_read: true } : n)));
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    }

    if (closeDropdown) setIsOpen(false);

    const parsed = parseNotificationMeta(notif?.message);
    if (notif.type?.startsWith('resource_') && isFreePlan) return;

    const ticketId = parsed.ticketId || (notif.message || '').match(/\[\[ticket:([^\]]+)\]\]/)?.[1];
    applyNotificationClick(
      resolveNotificationClick({
        type: notif.type,
        sourceUrl: parsed.sourceUrl,
        sourceApp: parsed.sourceApp,
        postId: notif.post_id,
        ticketId,
      }),
      { navigate, openResolvedTicket }
    );
  };

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) loadNotifications();
  };

  const handleNotificationClick = async (notif) => {
    await openNotification(notif);
  };

  const handleToastOpen = async (toast) => {
    dismissToast(toast.id);
    const notif = notifications.find((n) => n.id === toast.notificationId)
      || { id: toast.notificationId, type: toast.type, message: toast.body, is_read: false };
    await openNotification(notif, { closeDropdown: true });
  };

  const handleMarkAllRead = async () => {
    if (!user?.id) return;
    await markAllAsRead(user.id);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  if (!user) return null;

  return (
    <>
      <div className="relative">
        <button
          ref={bellRef}
          onClick={handleBellClick}
          className="relative p-2 rounded-lg hover:bg-[rgba(0,173,239,0.06)] transition-all duration-200 group"
          title="Notifications"
        >
          <Icon
            name="Bell"
            size={20}
            className={`transition-colors duration-200 ${
              unreadCount > 0
                ? 'text-[#00adef]'
                : 'text-gray-500 group-hover:text-[#0088bd]'
            }`}
          />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#fca000] text-white text-[10px] font-bold px-1 shadow-lg animate-in fade-in zoom-in duration-200">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute right-0 z-[1100] mt-2 max-h-[min(480px,70dvh)] w-[min(20rem,calc(100vw-1.25rem))] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 sm:w-96"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-slate-900">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[rgba(252,160,0,0.15)] text-[#c47d00]">
                    {unreadCount} new
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs font-semibold text-[#00adef] hover:text-[#0088bd] transition-colors"
                >
                  Mark all read
                </button>
              )}
            </div>

            <div className="overflow-y-auto max-h-[400px]">
              {isLoading && notifications.length === 0 ? (
                <div className="px-3 py-2">
                  <div className="flex justify-center py-8">
                    <MealPrepLoader size="sm" message="Loading notifications…" />
                  </div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Icon name="BellOff" size={32} className="mb-2 opacity-40" />
                  <p className="text-sm font-medium">No notifications yet</p>
                  <p className="text-xs mt-1">You will see community updates here</p>
                </div>
              ) : (
                notifications.map((notif) => {
                  const meta = TYPE_META[notif.type] || TYPE_META.comment;
                  const parsed = parseNotificationMeta(notif?.message);
                  return (
                    <button
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 ${
                        !notif.is_read ? 'bg-[rgba(0,173,239,0.06)]' : ''
                      }`}
                    >
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${meta.bg}`}>
                        <Icon name={meta.icon} size={16} className={meta.color} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-snug ${!notif.is_read ? 'font-semibold text-slate-900' : 'text-slate-600'}`}>
                          {formatNotificationMessage(notif)}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-[11px] text-slate-400">
                            {timeAgo(notif.created_at)}
                          </p>
                          {parsed?.sourceLabel && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-[rgba(0,173,239,0.1)] text-[#0088bd] border border-[rgba(0,173,239,0.2)]">
                              {parsed.sourceLabel}
                            </span>
                          )}
                        </div>
                      </div>

                      {!notif.is_read && (
                        <div className="flex-shrink-0 mt-2">
                          <div className="w-2 h-2 rounded-full bg-[#00adef]" />
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {notifications.length > 0 && (
              <div className="border-t border-slate-200 px-4 py-2 bg-slate-50">
                <button
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/community');
                  }}
                  className="w-full text-center text-xs font-semibold text-[#00adef] hover:text-[#0088bd] py-1 transition-colors"
                >
                  View all in Community
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {typeof document !== 'undefined'
        ? createPortal(
          <NotificationLiveToasts
            toasts={liveToasts}
            onOpen={handleToastOpen}
            onDismiss={dismissToast}
          />,
          document.body,
        )
        : null}
    </>
  );
};

export default NotificationBell;
